import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getHeadcountReport, downloadReport, type HeadcountRow } from '../api/reports.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { Alert } from '../components/Alert.js';

export const reportsHeadcountRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/reports/headcount',
  component: ReportsHeadcountPage,
});

const rate = (r: number | null): string => (r === null ? '—' : `${(r * 100).toFixed(1)}%`);
const grp = (g: string | null): string => g ?? '(none)';
/** Thousands-separated for display (integers stay integer; averageHeadcount keeps its .5). */
const num = (n: number): string => n.toLocaleString('en-US');

function ReportsHeadcountPage(): JSX.Element {
  const [start, setStart] = useState('2026-01-01');
  const [end, setEnd] = useState('2026-03-31');
  const [dept, setDept] = useState('');
  const filters = { periodStart: start, periodEnd: end, ...(dept ? { department: dept } : {}) };
  const q = useQuery({
    queryKey: ['reports', 'headcount', start, end, dept],
    queryFn: () => getHeadcountReport(filters),
    enabled: Boolean(start && end),
  });
  const [dlErr, setDlErr] = useState<unknown>(null);
  const download = async (format: 'csv' | 'pdf'): Promise<void> => {
    setDlErr(null);
    try { await downloadReport('headcount', filters, format); } catch (e) { setDlErr(e); }
  };

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Headcount &amp; turnover</h1>
        <p className="text-sm text-gray-400">Management reporting — how your headcount is moving, and turnover over a period. (Distinct from statutory Compliance reports.)</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-900 p-4">
        <label className="text-sm text-gray-300 space-y-1"><span>From</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Period start" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>To</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="Period end" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Department <span className="text-gray-500">(optional)</span></span>
          <input value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department filter" placeholder="All" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={() => void download('csv')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">CSV (opens in Excel)</button>
          <button type="button" onClick={() => void download('pdf')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">PDF</button>
        </div>
      </div>

      {/* A 402 PLAN_REQUIRED renders the upgrade prompt → /billing (self-serve). */}
      <ApiErrorNotice error={q.error} fallback="Could not load the report." />
      <ApiErrorNotice error={dlErr} fallback="Download failed." />

      {q.isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}

      {q.data ? (
        <div className="space-y-6">
          <TotalCard total={q.data.total} note={q.data.meta.turnoverNote} />
          <Table title="By department" rows={q.data.byDepartment} />
          <Table title="By country" rows={q.data.byCountry} />
        </div>
      ) : null}
    </section>
  );
}

function TotalCard({ total, note }: { total: HeadcountRow; note: string }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-4 space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Total</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <Metric label="Headcount (start → end)" value={`${num(total.headcountStart)} → ${num(total.headcountEnd)}`} />
        <Metric label="Average headcount" value={num(total.averageHeadcount)} />
        <Metric label="New hires" value={num(total.newHires)} />
        <Metric label="Terminations" value={num(total.terminations)} />
        <Metric label="Turnover rate" value={rate(total.turnoverRate)} />
      </div>
      {/* D2: show the arithmetic — terminations ÷ average — so the rate is checkable. */}
      <p className="text-xs text-gray-500">
        {num(total.terminations)} terminations ÷ {num(total.averageHeadcount)} average headcount = {rate(total.turnoverRate)}
      </p>
      <p className="text-xs text-gray-500">{note}</p>
    </div>
  );
}
function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return <div><div className="text-xs text-gray-500">{label}</div><div className="text-gray-200">{value}</div></div>;
}

function Table({ title, rows }: { title: string; rows: HeadcountRow[] }): JSX.Element {
  if (rows.length === 0) return <Alert variant="info" compact>No {title.toLowerCase()} data for this period.</Alert>;
  return (
    <div className="space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">{title}</h2>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 text-xs">
          <th className="py-1 pr-4">Group</th><th className="py-1 pr-4 text-right">End</th><th className="py-1 pr-4 text-right">New</th><th className="py-1 pr-4 text-right">Term.</th><th className="py-1 pr-4 text-right">Turnover</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.group ?? 'none'}`} className="border-t border-gray-900">
              <td className="py-2 pr-4 text-gray-200">{grp(r.group)}</td>
              <td className="py-2 pr-4 text-right text-gray-300">{num(r.headcountEnd)}</td>
              <td className="py-2 pr-4 text-right text-gray-300">{num(r.newHires)}</td>
              <td className="py-2 pr-4 text-right text-gray-300">{num(r.terminations)}</td>
              <td className="py-2 pr-4 text-right text-gray-300">{rate(r.turnoverRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
